import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Save,
  Trash2,
  Plus,
  X,
  AlertCircle,
  Loader2,
  TrendingUp,
  Settings,
  PowerOff,
  Activity,
  Clock,
  BarChart3,
  RefreshCw,
  Filter,
  Bell,
  Check,
  Info,
  Zap,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "../hooks/useToast";
import { useAuth } from "../components/features/AuthContext";
import { tradingApi, apiRequest } from "../components/lib/queryClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { CustomModelAddStocks } from "../components/CustomAddStocksModal";
import { ApiResponse } from "../components/lib/utils";
import {
  CryptoPair,
  FilterApiSchema,
  StockObject,
} from "./LeaderAssetManagement";
import { Bucket } from "./bucket-management";

// ========================= TYPES =========================

type StockInstrument = {
  security_id: number;
  symbol_name: string;
  underlying_symbol: string;
  instrument_type: string;
  instrument: string;
  exchange_segment: string;
  fno_true: boolean;
};

interface AlertRow {
  timeframe: string;
  lookback_candles: number;
}

interface AlertConfig {
  _id?: string;
  created_by: string;
  category: "crypto" | "stocks";
  symbols: string[] | StockInstrument[];
  rows: AlertRow[];
  is_active: boolean;
  updated_at: string;
  manager_running?: boolean;
}

interface AlertEvent {
  id: string;
  category: string;
  symbol: string;
  timeframe: string;
  lookback: number;
  volume: number;
  previous_max_volume: number;
  percentage_increase: number;
  candle_close_time: string;
  triggered_at: string;
}

interface FiltrationAlertConfig {
  _id?: string;
  created_by?: string;
  category: "crypto" | "stocks";
  use_leader: boolean;
  leader?: CryptoPair | StockObject;
  leader_strategy_ids?: string[];
  bucket_ids?: string[];
  scan_interval_seconds?: number;
  is_active?: boolean;
  manager_running?: boolean;
  updated_at?: string;
}

// ========================= CONSTANTS =========================

const TIMEFRAMES = {
  crypto: ["5s", "15s", "30s", "1m", "3m", "5m", "15m", "30m", "1h", "4h"],
  stocks: ["5s", "15s", "30s", "1m", "3m", "5m", "15m", "30m", "1h", "4h", "1d"],
} as const;

const LOOKBACK_OPTIONS = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30];
const DEFAULT_ROW: AlertRow = { timeframe: "15m", lookback_candles: 9 };

// ========================= HOOKS =========================

const useLiquidationConfig = (category: "stocks" | "crypto", enabled: boolean) => {
  return useQuery<AlertConfig | null>({
    queryKey: ["liquidation-config", category],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({ category });
        const res = await tradingApi<ApiResponse<AlertConfig>>(
          "GET",
          `api/liquidation-alerts/config?${params}`,
          category
        );
        return res.data;
      } catch (error: any) {
        if (error.message?.includes("404")) return null;
        throw error;
      }
    },
    enabled,
  });
};

const useLiquidationEvents = (
  category: "crypto" | "stocks",
  filterSymbol: string,
  filterTimeframe: string,
  filterHours: number,
  enabled: boolean
) => {
  return useQuery<{ events: AlertEvent[]; total: number }>({
    queryKey: ["liquidation-events", category, filterSymbol, filterTimeframe, filterHours],
    queryFn: async () => {
      const params = new URLSearchParams({
        hours: filterHours.toString(),
        limit: "100",
      });
      if (filterSymbol) params.append("symbol", filterSymbol);
      if (filterTimeframe) params.append("timeframe", filterTimeframe);
      const res = await tradingApi<ApiResponse<{ events: AlertEvent[]; total: number }>>(
        "GET",
        `api/liquidation-alerts/events?${params}`,
        category
      );
      return res.data;
    },
    enabled,
    refetchInterval: 30000,
  });
};

const useFiltrationConfig = (category: "stocks" | "crypto", enabled: boolean) => {
  return useQuery<FiltrationAlertConfig | null>({
    queryKey: ["filtration-alert-config", category],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({ category });
        const res = await tradingApi<ApiResponse<FiltrationAlertConfig>>(
          "GET",
          `api/filtration-alerts/config?${params}`,
          category
        );
        return res.data;
      } catch (error: any) {
        if (error.message?.includes("404")) return null;
        throw error;
      }
    },
    enabled,
  });
};

// ========================= SUB-COMPONENTS =========================

/** Unified status pill used in both card headers */
function RunningStatusBadge({ running }: { running: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide border ${
        running
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-100 text-slate-500 border-slate-200"
      }`}
    >
      {running ? (
        <>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          Running
        </>
      ) : (
        <>
          <PowerOff className="h-3 w-3" />
          Stopped
        </>
      )}
    </span>
  );
}

/** Active / disabled inline toggle control */
function ActiveToggle({
  checked,
  onCheckedChange,
  disabled,
  id,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  id: string;
}) {
  return (
    <div
      className={`flex items-center gap-2.5 h-10 px-3.5 rounded-xl border transition-colors ${
        checked
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-200 bg-white"
      }`}
    >
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        id={id}
        className="data-[state=checked]:bg-emerald-500"
      />
      <Label htmlFor={id} className="text-sm font-medium cursor-pointer select-none">
        {checked ? (
          <span className="text-emerald-600">Active</span>
        ) : (
          <span className="text-slate-400">Inactive</span>
        )}
      </Label>
    </div>
  );
}

/** Stat summary chip */
function StatChip({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: any;
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-white border ${color}`}>
      <Icon className="h-3.5 w-3.5 opacity-60" />
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs font-bold">{value}</span>
    </div>
  );
}

// ========================= MAIN COMPONENT =========================

export default function TelegramAlertsPage() {
  const { currentTradingMode, user } = useAuth();
  const category = currentTradingMode as "crypto" | "stocks";
  const isCrypto = category === "crypto";
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<"liquidation" | "filtration">("liquidation");

  // ── Liquidation state ──
  const [cryptoSymbols, setCryptoSymbols] = useState<string[]>([]);
  const [stockSymbols, setStockSymbols] = useState<StockInstrument[]>([]);
  const [tempCryptoSymbols, setTempCryptoSymbols] = useState<string[]>([]);
  const [symbolInput, setSymbolInput] = useState("");
  const [rows, setRows] = useState<AlertRow[]>([DEFAULT_ROW]);
  const [isActive, setIsActive] = useState(false);
  const [symbolModalOpen, setSymbolModalOpen] = useState(false);
  const [filterSymbol, setFilterSymbol] = useState("");
  const [filterTimeframe, setFilterTimeframe] = useState("");
  const [filterHours, setFilterHours] = useState(24);

  // ── Filtration state ──
  const [filtUseLeader, setFiltUseLeader] = useState(false);
  const [filtLeader, setFiltLeader] = useState<CryptoPair | StockObject | undefined>(undefined);
  const [filtLeaderStrategyIds, setFiltLeaderStrategyIds] = useState<string[]>([]);
  const [filtBucketIds, setFiltBucketIds] = useState<string[]>([]);
  const [filtScanIntervalSeconds, setFiltScanIntervalSeconds] = useState(15);
  const [filtIsActive, setFiltIsActive] = useState(false);

  // ==================== QUERIES ====================

  const { data: config } = useLiquidationConfig(category, !!category);
  const { data: eventsData, isLoading: isLoadingEvents, refetch: refetchEvents } =
    useLiquidationEvents(category, filterSymbol, filterTimeframe, filterHours, !!category);
  const { data: filtrationConfig } = useFiltrationConfig(category, !!category);

  const { data: leaders } = useQuery<(StockObject | CryptoPair)[]>({
    queryKey: ["api/leaders", category],
    queryFn: async () => {
      const params = new URLSearchParams({ category });
      const res = await apiRequest<ApiResponse<(StockObject | CryptoPair)[]>>(
        "GET",
        `api/leaders?${params}`
      );
      return res.data;
    },
    enabled: !!category,
  });

  const { data: strategies } = useQuery<FilterApiSchema[]>({
    queryKey: ["api/strategies-leader", category],
    queryFn: async () => {
      const params = new URLSearchParams({ category, type: "LEADER" });
      const res = await apiRequest<ApiResponse<FilterApiSchema[]>>(
        "GET",
        `api/strategies?${params}`
      );
      return res.data;
    },
    enabled: !!category,
  });

  const { data: buckets } = useQuery<Bucket[]>({
    queryKey: ["api/buckets", user?.userId, user?.role, currentTradingMode],
    queryFn: async () => {
      const url = `api/buckets?userId=${user?.userId}&role=${user?.role}&category=${currentTradingMode}`;
      const res = await apiRequest<ApiResponse<Bucket[]>>("GET", url);
      return res.data;
    },
    enabled: !!user?.userId && !!user?.role && !!currentTradingMode,
  });

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (config) {
      if (isCrypto) setCryptoSymbols(config.symbols as string[]);
      else setStockSymbols(config.symbols as StockInstrument[]);
      setRows(config.rows || [DEFAULT_ROW]);
      setIsActive(config.is_active || false);
    }
  }, [config, isCrypto]);

  useEffect(() => {
    if (symbolModalOpen) {
      setTempCryptoSymbols([]);
      setSymbolInput("");
    }
  }, [symbolModalOpen]);

  useEffect(() => {
    if (filtrationConfig) {
      setFiltUseLeader(filtrationConfig.use_leader ?? false);
      setFiltLeader(filtrationConfig.leader ?? undefined);
      setFiltLeaderStrategyIds(filtrationConfig.leader_strategy_ids ?? []);
      setFiltBucketIds(filtrationConfig.bucket_ids ?? []);
      setFiltScanIntervalSeconds(filtrationConfig.scan_interval_seconds ?? 15);
      setFiltIsActive(filtrationConfig.is_active ?? false);
    }
  }, [filtrationConfig]);

  // ==================== LIQUIDATION MUTATIONS ====================

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const payload = {
        symbols: isCrypto ? cryptoSymbols : stockSymbols,
        rows,
        is_active: isActive,
      };
      return tradingApi<ApiResponse<any>>("POST", "api/liquidation-alerts/config", category, payload);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["liquidation-config", category] });
      queryClient.invalidateQueries({ queryKey: ["liquidation-events", category] });
      toast({ title: "Success", description: res.message || "Configuration saved successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const { mutate: toggleAlerts, isPending: isToggling } = useMutation({
    mutationFn: async (is_active: boolean) => {
      const params = new URLSearchParams({ category, is_active: is_active.toString() });
      return tradingApi<ApiResponse<any>>("POST", `api/liquidation-alerts/toggle?${params}`, category);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["liquidation-config", category] });
      toast({ title: "Success", description: res.message });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const { mutate: deleteConfig, isPending: isDeleting } = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams({ category });
      return tradingApi<ApiResponse<any>>("DELETE", `api/liquidation-alerts/config?${params}`, category);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["liquidation-config", category] });
      toast({ title: "Deleted", description: res.message });
      if (isCrypto) setCryptoSymbols([]);
      else setStockSymbols([]);
      setRows([DEFAULT_ROW]);
      setIsActive(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // ==================== FILTRATION MUTATIONS ====================

  const { mutate: saveFiltrationConfig, isPending: isSavingFiltration } = useMutation({
    mutationFn: async () => {
      const payload = {
        use_leader: filtUseLeader,
        leader: filtUseLeader ? filtLeader : undefined,
        leader_strategy_ids: filtUseLeader ? filtLeaderStrategyIds : [],
        bucket_ids: filtBucketIds,
        scan_interval_seconds: filtScanIntervalSeconds,
        is_active: filtIsActive,
      };
      return tradingApi<ApiResponse<any>>("POST", "api/filtration-alerts/config", category, payload);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["filtration-alert-config", category] });
      toast({ title: "Success", description: res.message || "Filtration config saved" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const { mutate: toggleFiltrationAlerts, isPending: isTogglingFiltration } = useMutation({
    mutationFn: async (is_active: boolean) => {
      const params = new URLSearchParams({ category, is_active: is_active.toString() });
      return tradingApi<ApiResponse<any>>(
        "POST",
        `api/filtration-alerts/toggle?${params}`,
        category
      );
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["filtration-alert-config", category] });
      toast({ title: "Success", description: res.message });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const { mutate: deleteFiltrationConfig, isPending: isDeletingFiltration } = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams({ category });
      return tradingApi<ApiResponse<any>>(
        "DELETE",
        `api/filtration-alerts/config?${params}`,
        category
      );
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["filtration-alert-config", category] });
      toast({ title: "Deleted", description: res.message });
      setFiltUseLeader(false);
      setFiltLeader(undefined);
      setFiltLeaderStrategyIds([]);
      setFiltBucketIds([]);
      setFiltScanIntervalSeconds(15);
      setFiltIsActive(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // ==================== LIQUIDATION HANDLERS ====================

  const handleAddCryptoSymbol = useCallback((input: string) => {
    if (!input.trim()) return;
    const normalized = input.trim().toUpperCase();
    setTempCryptoSymbols((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized]
    );
    setSymbolInput("");
  }, []);

  const handleRemoveCryptoSymbol = useCallback((symbol: string) => {
    setCryptoSymbols((prev) => prev.filter((s) => s !== symbol));
  }, []);

  const handleRemoveTempCryptoSymbol = useCallback((symbol: string) => {
    setTempCryptoSymbols((prev) => prev.filter((s) => s !== symbol));
  }, []);

  const handleSaveCryptoSymbols = useCallback(() => {
    setCryptoSymbols((prev) => {
      const newSymbols = tempCryptoSymbols.filter((s) => !prev.includes(s));
      return [...prev, ...newSymbols];
    });
    setTempCryptoSymbols([]);
    setSymbolModalOpen(false);
  }, [tempCryptoSymbols]);

  const handleAddStockSymbols = useCallback((stocks: StockInstrument[]) => {
    setStockSymbols((prev) => {
      const existingIds = new Set(prev.map((s) => s.security_id));
      const newStocks = stocks.filter((s) => !existingIds.has(s.security_id));
      return [...prev, ...newStocks];
    });
    setSymbolModalOpen(false);
  }, []);

  const handleRemoveStockSymbol = useCallback((stock: StockInstrument) => {
    setStockSymbols((prev) => prev.filter((s) => s.security_id !== stock.security_id));
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, DEFAULT_ROW]);
  }, []);

  const handleRemoveRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRowChange = useCallback((index: number, field: keyof AlertRow, value: any) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }, []);

  const handleSaveConfig = useCallback(() => {
    const symbolCount = isCrypto ? cryptoSymbols.length : stockSymbols.length;
    if (symbolCount === 0) {
      toast({ title: "Validation Error", description: "Please add at least one symbol", variant: "destructive" });
      return;
    }
    if (rows.length === 0) {
      toast({ title: "Validation Error", description: "Please add at least one timeframe row", variant: "destructive" });
      return;
    }
    saveConfig();
  }, [isCrypto, cryptoSymbols.length, stockSymbols.length, rows.length, saveConfig]);

  const handleToggleActive = useCallback(
    (value: boolean) => {
      setIsActive(value);
      if (config) toggleAlerts(value);
    },
    [config, toggleAlerts]
  );

  const handleToggleFiltrationActive = useCallback(
    (value: boolean) => {
      setFiltIsActive(value);
      if (filtrationConfig) toggleFiltrationAlerts(value);
    },
    [filtrationConfig, toggleFiltrationAlerts]
  );

  const handleClearFilters = useCallback(() => {
    setFilterSymbol("");
    setFilterTimeframe("");
    setFilterHours(24);
  }, []);

  // ==================== FILTRATION HANDLERS ====================

  const handleToggleLeaderStrategy = useCallback((strategyId: string) => {
    setFiltLeaderStrategyIds((prev) =>
      prev.includes(strategyId)
        ? prev.filter((id) => id !== strategyId)
        : [...prev, strategyId]
    );
  }, []);

  const handleToggleBucket = useCallback((bucketId: string) => {
    setFiltBucketIds((prev) =>
      prev.includes(bucketId) ? prev.filter((id) => id !== bucketId) : [...prev, bucketId]
    );
  }, []);

  const handleSaveFiltration = useCallback(() => {
    if (filtUseLeader && !filtLeader) {
      toast({ title: "Validation Error", description: "Please select a leader coin", variant: "destructive" });
      return;
    }
    if (filtUseLeader && filtLeaderStrategyIds.length === 0) {
      toast({ title: "Validation Error", description: "Please select at least one leader strategy", variant: "destructive" });
      return;
    }
    if (filtBucketIds.length === 0) {
      toast({ title: "Validation Error", description: "Please select at least one bucket", variant: "destructive" });
      return;
    }
    saveFiltrationConfig();
  }, [filtUseLeader, filtLeader, filtLeaderStrategyIds, filtBucketIds, saveFiltrationConfig]);

  // ==================== COMPUTED ====================

  const currentSymbols = useMemo(
    () => (isCrypto ? cryptoSymbols : stockSymbols),
    [isCrypto, cryptoSymbols, stockSymbols]
  );
  const symbolCount = currentSymbols.length;
  const availableTimeframes = TIMEFRAMES[isCrypto ? "crypto" : "stocks"];

  const selectedStrategiesLabel = useMemo(() => {
    if (!filtLeaderStrategyIds.length) return "";
    return (
      strategies
        ?.filter((s) => filtLeaderStrategyIds.includes(s.id ?? ""))
        .map((s) => s.function_name)
        .join(", ") ?? ""
    );
  }, [filtLeaderStrategyIds, strategies]);

  // ==================== GUARD ====================

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="max-w-md shadow-xl border-0">
          <CardContent className="pt-8 pb-8 px-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="h-14 w-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg">Admin Access Required</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Only administrators can configure Telegram alerts.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/80">
      <div className="px-4 md:px-6  py-10 space-y-8">

        {/* ── Page Header ── */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
                <Bell className="h-6 w-6 text-white" />
              </div>
              <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Telegram Alerts</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {isCrypto ? "Crypto" : "Stocks"} alert configuration &amp; monitoring
              </p>
            </div>
          </div>

          {/* Quick-glance status strip */}
          <div className="hidden md:flex items-center gap-2">
            <StatChip
              icon={Zap}
              label="Liquidation"
              value={config?.is_active ? "On" : "Off"}
              color={config?.is_active ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500"}
            />
            <StatChip
              icon={Shield}
              label="Filtration"
              value={filtrationConfig?.is_active ? "On" : "Off"}
              color={filtrationConfig?.is_active ? "border-indigo-200 text-indigo-700" : "border-slate-200 text-slate-500"}
            />
            <StatChip
              icon={BarChart3}
              label="Symbols"
              value={symbolCount}
              color="border-slate-200 text-slate-600"
            />
          </div>
        </div>

        {/* ── Tab Switcher ── */}
        <div className="flex gap-1 p-1 bg-slate-100/80 backdrop-blur-sm rounded-2xl w-fit border border-slate-200/60 shadow-sm">
          <button
            onClick={() => setActiveTab("liquidation")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              activeTab === "liquidation"
                ? "bg-white text-slate-900 shadow-sm border border-slate-200/60"
                : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
            }`}
          >
            <Bell className="h-4 w-4" />
            Liquidation Alerts
            {config?.manager_running && (
              <span className="ml-0.5 relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("filtration")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              activeTab === "filtration"
                ? "bg-white text-slate-900 shadow-sm border border-slate-200/60"
                : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
            }`}
          >
            <Filter className="h-4 w-4" />
            Filtration Alerts
            {filtrationConfig?.manager_running && (
              <span className="ml-0.5 relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
              </span>
            )}
          </button>
        </div>

        {/* ══════════════════════════════════
            TAB 1 — LIQUIDATION ALERTS
        ══════════════════════════════════ */}
        {activeTab === "liquidation" && (
          <>
            {/* Config Card */}
            <Card className="border border-slate-200/80 shadow-xl shadow-slate-100 bg-white overflow-hidden">
              {/* Decorative top stripe */}
              <div className="h-1 w-full bg-gradient-to-r from-red-500 via-orange-500 to-amber-400" />

              <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-red-50/60 to-orange-50/40 pb-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-md shadow-red-200">
                      <Settings className="h-4.5 w-4.5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-bold text-slate-900">
                        Alert Configuration
                      </CardTitle>
                      <CardDescription className="text-xs text-slate-500 mt-0.5">
                        Volume breakout detection for {category} symbols
                      </CardDescription>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 flex-wrap">
                    {config?.manager_running !== undefined && (
                      <RunningStatusBadge running={config.manager_running} />
                    )}

                    <ActiveToggle
                      checked={isActive}
                      onCheckedChange={handleToggleActive}
                      disabled={isToggling}
                      id="alerts-active"
                    />

                    {config && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteConfig()}
                        disabled={isDeleting}
                        className="h-10 w-10 p-0 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 space-y-7">
                {/* Symbols */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-semibold text-slate-900">
                        Monitored Symbols
                      </Label>
                      <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">
                        {symbolCount}
                      </span>
                    </div>
                    <Button
                      onClick={() => setSymbolModalOpen(true)}
                      size="sm"
                      className="gap-1.5 h-8 px-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white shadow-sm shadow-red-200 border-0"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Symbols
                    </Button>
                  </div>

                  {symbolCount > 0 ? (
                    <div className="flex flex-wrap gap-2 p-4 border border-dashed border-slate-200 rounded-xl bg-slate-50/60">
                      {currentSymbols.map((item) => {
                        const label = isCrypto
                          ? (item as string)
                          : (item as StockInstrument).underlying_symbol;
                        return (
                          <Badge
                            key={isCrypto ? (item as string) : (item as StockInstrument).security_id}
                            variant="secondary"
                            className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 text-slate-700 shadow-sm hover:border-red-200 transition-colors"
                          >
                            {label}
                            <button
                              onClick={() =>
                                isCrypto
                                  ? handleRemoveCryptoSymbol(item as string)
                                  : handleRemoveStockSymbol(item as StockInstrument)
                              }
                              className="ml-2 hover:text-red-500 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/40">
                      <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                        <TrendingUp className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-500 font-medium">No symbols configured</p>
                      <p className="text-xs text-slate-400 mt-1">Add symbols to start monitoring</p>
                    </div>
                  )}
                </div>

                <Separator className="bg-slate-100" />

                {/* Timeframe Rows */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-semibold text-slate-900">
                        Timeframe Configuration
                      </Label>
                      <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">
                        {rows.length}
                      </span>
                    </div>
                    <Button
                      onClick={handleAddRow}
                      size="sm"
                      className="gap-1.5 h-8 px-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white shadow-sm shadow-red-200 border-0"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Row
                    </Button>
                  </div>

                  <div className="space-y-2.5">
                    {rows.map((row, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-4 border border-slate-200/80 rounded-xl bg-white hover:border-slate-300 transition-colors shadow-sm"
                      >
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-red-600">{index + 1}</span>
                        </div>
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs text-slate-500 font-medium">Timeframe</Label>
                            <Select
                              value={row.timeframe}
                              onValueChange={(value) => handleRowChange(index, "timeframe", value)}
                            >
                              <SelectTrigger className="h-9 text-sm bg-slate-50 border-slate-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {availableTimeframes.map((tf) => (
                                  <SelectItem key={tf} value={tf}>{tf}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-slate-500 font-medium">Lookback Candles</Label>
                            <Select
                              value={row.lookback_candles.toString()}
                              onValueChange={(value) => handleRowChange(index, "lookback_candles", parseInt(value))}
                            >
                              <SelectTrigger className="h-9 text-sm bg-slate-50 border-slate-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {LOOKBACK_OPTIONS.map((num) => (
                                  <SelectItem key={num} value={num.toString()}>{num} candles</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRow(index)}
                          disabled={rows.length === 1}
                          className="h-9 w-9 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <Button
                    onClick={handleSaveConfig}
                    disabled={isSaving || symbolCount === 0}
                    className="gap-2 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white shadow-md shadow-red-200 border-0 px-6"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Triggered Alerts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md shadow-orange-200">
                    <Activity className="h-4.5 w-4.5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Triggered Alerts</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Recent volume breakout detections</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchEvents()}
                  className="gap-2 h-9 border-slate-200 hover:bg-slate-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh
                </Button>
              </div>

              {/* Filters */}
              <Card className="border border-slate-200/80 shadow-sm bg-slate-50/60">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500 font-medium">Symbol</Label>
                      <Input
                        placeholder="Filter by symbol"
                        value={filterSymbol}
                        onChange={(e) => setFilterSymbol(e.target.value.toUpperCase())}
                        className="h-9 text-sm bg-white border-slate-200"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500 font-medium">Timeframe</Label>
                      <Select value={filterTimeframe} onValueChange={setFilterTimeframe}>
                        <SelectTrigger className="h-9 text-sm bg-white border-slate-200">
                          <SelectValue placeholder="All timeframes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All timeframes</SelectItem>
                          {availableTimeframes.map((tf) => (
                            <SelectItem key={tf} value={tf}>{tf}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500 font-medium">Time Range</Label>
                      <Select value={filterHours.toString()} onValueChange={(v) => setFilterHours(parseInt(v))}>
                        <SelectTrigger className="h-9 text-sm bg-white border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Last 1 hour</SelectItem>
                          <SelectItem value="6">Last 6 hours</SelectItem>
                          <SelectItem value="24">Last 24 hours</SelectItem>
                          <SelectItem value="72">Last 3 days</SelectItem>
                          <SelectItem value="168">Last 7 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        onClick={handleClearFilters}
                        className="w-full h-9 text-sm border-slate-200 hover:bg-white"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200/80 shadow-xl shadow-slate-100 bg-white overflow-hidden">
                <div className="h-0.5 w-full bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-300" />
                <CardContent className="p-0">
                  {isLoadingEvents ? (
                    <div className="flex flex-col justify-center items-center py-16 gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                      <p className="text-sm text-slate-400">Loading events…</p>
                    </div>
                  ) : eventsData?.events.length ? (
                    <div className="overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/80 hover:bg-slate-50 border-b border-slate-100">
                            <TableHead className="font-semibold text-xs text-slate-500 uppercase tracking-wider">Symbol</TableHead>
                            <TableHead className="font-semibold text-xs text-slate-500 uppercase tracking-wider">Timeframe</TableHead>
                            <TableHead className="font-semibold text-xs text-slate-500 uppercase tracking-wider">Volume</TableHead>
                            <TableHead className="font-semibold text-xs text-slate-500 uppercase tracking-wider">Previous Max</TableHead>
                            <TableHead className="font-semibold text-xs text-slate-500 uppercase tracking-wider">Increase</TableHead>
                            <TableHead className="font-semibold text-xs text-slate-500 uppercase tracking-wider">Triggered</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {eventsData.events.map((event) => (
                            <TableRow key={event.id} className="hover:bg-slate-50/60 border-b border-slate-50 transition-colors">
                              <TableCell className="font-bold text-slate-900">{event.symbol}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs border-slate-200 text-slate-600">
                                  {event.timeframe}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-slate-700 font-medium">{event.volume.toLocaleString()}</TableCell>
                              <TableCell className="text-slate-500">{event.previous_max_volume.toLocaleString()}</TableCell>
                              <TableCell>
                                <Badge
                                  className={`text-xs font-semibold border-0 ${
                                    event.percentage_increase > 50
                                      ? "bg-red-100 text-red-700"
                                      : event.percentage_increase > 25
                                      ? "bg-orange-100 text-orange-700"
                                      : "bg-emerald-100 text-emerald-700"
                                  }`}
                                >
                                  +{event.percentage_increase.toFixed(1)}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="h-3 w-3" />
                                  {new Date(event.triggered_at).toLocaleString()}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                        <BarChart3 className="h-7 w-7 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">No alerts triggered yet</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Configure symbols and enable alerts to start monitoring
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* ══════════════════════════════════
            TAB 2 — FILTRATION ALERTS
        ══════════════════════════════════ */}
        {activeTab === "filtration" && (
          <Card className="border border-slate-200/80 shadow-xl shadow-slate-100 bg-white overflow-hidden">
            {/* Decorative top stripe */}
            <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-400" />

            <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-indigo-50/60 to-blue-50/40 pb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-md shadow-indigo-200">
                    <Filter className="h-4.5 w-4.5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-slate-900">
                      Filtration Configuration
                    </CardTitle>
                    <CardDescription className="text-xs text-slate-500 mt-0.5">
                      Leader-based signal filtering for {category} Telegram alerts
                    </CardDescription>
                  </div>
                </div>

                {/* ── NEW: same controls as Liquidation ── */}
                <div className="flex items-center gap-2.5 flex-wrap">
                  {filtrationConfig?.manager_running !== undefined && (
                    <RunningStatusBadge running={filtrationConfig.manager_running} />
                  )}

                  <ActiveToggle
                    checked={filtIsActive}
                    onCheckedChange={handleToggleFiltrationActive}
                    disabled={isTogglingFiltration}
                    id="filtration-active"
                  />

                  {filtrationConfig && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteFiltrationConfig()}
                      disabled={isDeletingFiltration}
                      className="h-10 w-10 p-0 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                    >
                      {isDeletingFiltration ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {/* Info box */}
              <div className="flex gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
                <p className="text-xs leading-relaxed">
                  When <strong>Leader Following</strong> is enabled, the filtration service checks
                  the selected leader coin against the chosen strategies before dispatching a Telegram
                  alert. If the leader passes, the signal is sent — otherwise it is suppressed.
                  Use the toggle above to activate or pause this service without deleting the config.
                </p>
              </div>

              {/* Leader Following Toggle Row */}
              <div
                className={`flex flex-row items-start gap-4 p-5 rounded-xl border-2 transition-all cursor-pointer select-none ${
                  filtUseLeader
                    ? "border-indigo-200 bg-indigo-50/50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                onClick={() => setFiltUseLeader((v) => !v)}
              >
                <Checkbox
                  checked={filtUseLeader}
                  onCheckedChange={(checked) => setFiltUseLeader(!!checked)}
                  className="mt-0.5 border-2 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-indigo-600" />
                    <Label className="text-sm font-semibold text-slate-900 cursor-pointer">
                      Enable Leader Following
                    </Label>
                    {filtUseLeader && (
                      <Badge className="bg-indigo-100 text-indigo-700 border-0 text-xs px-2 py-0.5">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Gate every filtration alert behind a leader coin's strategy results. Alerts only
                    fire when the leader passes all selected strategies.
                  </p>
                </div>
              </div>

              {/* Leader & Strategy selectors */}
              {filtUseLeader && (
                <div className="space-y-5 ml-3 pl-5 border-l-2 border-indigo-200">

                  {/* Select Leader */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold flex items-center gap-2 text-slate-900">
                      Select Leader Coin
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-slate-400" />
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>The leader coin whose strategy results gate your alerts</p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>

                    <Select
                      value={filtLeader?._id ?? ""}
                      onValueChange={(value) => {
                        const selected = leaders?.find((l) => l._id === value);
                        setFiltLeader(selected ?? undefined);
                      }}
                    >
                      <SelectTrigger className="bg-white border-indigo-200 focus:ring-2 focus:ring-indigo-400 h-10">
                        <SelectValue placeholder="Choose a leader coin…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {leaders?.map((leader) => (
                          <SelectItem
                            key={leader._id}
                            value={leader._id!}
                            className="focus:bg-indigo-50"
                          >
                            {"symbol" in leader ? leader.symbol : leader.underlying_symbol}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Select Strategies */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold flex items-center gap-2 text-slate-900">
                      Leader Strategies
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-slate-400" />
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>All selected strategies must pass on the leader for an alert to fire</p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-between bg-white border-indigo-200 hover:border-indigo-300 focus:ring-2 focus:ring-indigo-400 text-left font-normal h-10"
                        >
                          <span className="text-sm truncate text-slate-600">
                            {filtLeaderStrategyIds.length > 0
                              ? `${filtLeaderStrategyIds.length} selected — ${selectedStrategiesLabel}`
                              : "Select leader strategies…"}
                          </span>
                          <Check className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                        </Button>
                      </PopoverTrigger>

                      <PopoverContent className="w-80 p-3 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-200">
                        <div className="space-y-1">
                          {strategies && strategies.length > 0 ? (
                            strategies.map((strategy) => {
                              const checked = filtLeaderStrategyIds.includes(strategy.id ?? "");
                              return (
                                <div
                                  key={strategy.id}
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-indigo-50 cursor-pointer border border-transparent hover:border-indigo-100 transition-all"
                                  onClick={() => handleToggleLeaderStrategy(strategy.id ?? "")}
                                >
                                  <Checkbox
                                    checked={checked}
                                    className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={() => handleToggleLeaderStrategy(strategy.id ?? "")}
                                  />
                                  <span className="font-medium text-slate-700 text-sm">
                                    {strategy.function_name}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-sm text-slate-400 text-center py-4">
                              No leader strategies available
                            </p>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Selected strategy chips */}
                    {filtLeaderStrategyIds.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {strategies
                          ?.filter((s) => filtLeaderStrategyIds.includes(s.id ?? ""))
                          .map((s) => (
                            <Badge
                              key={s.id}
                              variant="secondary"
                              className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 text-xs"
                            >
                              {s.function_name}
                              <button
                                onClick={() => handleToggleLeaderStrategy(s.id ?? "")}
                                className="ml-1.5 hover:text-red-500 transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bucket selectors (for standalone filtration loop instruments) */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-900">Buckets for Filtration Alerts</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between bg-white border-indigo-200 hover:border-indigo-300 focus:ring-2 focus:ring-indigo-400 text-left font-normal h-10"
                    >
                      <span className="text-sm truncate text-slate-600">
                        {filtBucketIds.length > 0
                          ? `${filtBucketIds.length} selected`
                          : "Select buckets for standalone scan…"}
                      </span>
                      <Check className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-3 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-200">
                    <div className="space-y-1">
                      {buckets && buckets.length > 0 ? (
                        buckets.map((bucket) => {
                          const checked = filtBucketIds.includes(bucket._id);
                          return (
                            <div
                              key={bucket._id}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-indigo-50 cursor-pointer border border-transparent hover:border-indigo-100 transition-all"
                              onClick={() => handleToggleBucket(bucket._id)}
                            >
                              <Checkbox
                                checked={checked}
                                className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                onClick={(e) => e.stopPropagation()}
                                onCheckedChange={() => handleToggleBucket(bucket._id)}
                              />
                              <span className="font-medium text-slate-700 text-sm">{bucket.name}</span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-slate-400 text-center py-4">No buckets available</p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-900">Standalone Scan Interval (seconds)</Label>
                <Input
                  type="number"
                  min={5}
                  value={filtScanIntervalSeconds}
                  onChange={(e) => setFiltScanIntervalSeconds(Math.max(5, Number(e.target.value || 15)))}
                  className="bg-white border-indigo-200 focus:ring-2 focus:ring-indigo-400 h-10"
                />
              </div>

              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button
                  onClick={handleSaveFiltration}
                  disabled={isSavingFiltration}
                  className="gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-md shadow-indigo-200 border-0 px-6"
                >
                  {isSavingFiltration ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Symbol Modal ── */}
      <Dialog open={symbolModalOpen} onOpenChange={setSymbolModalOpen}>
        <DialogContent className="sm:max-w-[500px] border-0 shadow-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Add {isCrypto ? "Crypto" : "Stock"} Symbols
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {isCrypto
                ? "Enter cryptocurrency symbols (e.g., BTCUSDT, ETHUSDT)"
                : "Select stocks to monitor for volume breakouts"}
            </DialogDescription>
          </DialogHeader>

          {isCrypto ? (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={symbolInput}
                  onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAddCryptoSymbol(symbolInput); }}
                  placeholder="Enter symbol (e.g., BTCUSDT)"
                  className="flex-1 bg-slate-50 border-slate-200"
                />
                <Button
                  type="button"
                  onClick={() => handleAddCryptoSymbol(symbolInput)}
                  disabled={!symbolInput.trim()}
                  className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white border-0"
                >
                  Add
                </Button>
              </div>

              {tempCryptoSymbols.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Symbols to add ({tempCryptoSymbols.length})
                  </Label>
                  <div className="flex flex-wrap gap-2 p-3 border border-dashed border-slate-200 rounded-xl bg-slate-50">
                    {tempCryptoSymbols.map((symbol) => (
                      <Badge key={symbol} variant="secondary" className="px-3 py-1.5 text-xs bg-white border border-slate-200">
                        {symbol}
                        <button
                          onClick={() => handleRemoveTempCryptoSymbol(symbol)}
                          className="ml-2 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="mt-4 gap-2">
                <Button variant="outline" onClick={() => setSymbolModalOpen(false)} className="border-slate-200">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveCryptoSymbols}
                  disabled={tempCryptoSymbols.length === 0}
                  className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white border-0"
                >
                  Save Symbols ({tempCryptoSymbols.length})
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <CustomModelAddStocks
              onAddStocks={handleAddStockSymbols}
              onClose={() => setSymbolModalOpen(false)}
              show={symbolModalOpen}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}